import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiUrl } from '@/config/api';
import { authenticatedFetchWithRetry } from '@/utils/apiUtils';
import { Header } from '@/components/shared';
import DashboardLayout from '@/components/shared/DashboardLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { Search, AlertTriangle } from '@/utils/icons';
import type { TeamHealthAthlete } from '@/types';
import AthleteHealthRow from './AthleteHealthRow';
import AthleteHealthExpanded from './AthleteHealthExpanded';
import BodyMapPanel from './BodyMapPanel';

type SortKey = 'name' | 'soreness' | 'rpe' | 'active_injury_count';
type SortDir = 'asc' | 'desc';

interface UserInfo {
  username: string;
  role?: string;
  full_name?: string;
  first_name?: string;
}

const TeamHealthDashboard: React.FC = () => {
  const { isDark } = useTheme();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [athletes, setAthletes] = useState<TeamHealthAthlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [teamGender, setTeamGender] = useState<'male' | 'female' | 'mixed'>('mixed');
  const [search, setSearch] = useState('');
  const [filterInjured, setFilterInjured] = useState<'all' | 'injured' | 'healthy'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Load user info from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('user_info');
    if (stored) {
      try { setUserInfo(JSON.parse(stored)); } catch { /* ignore */ }
    }
  }, []);

  const fetchTeamHealth = useCallback(async () => {
    setLoading(true);
    setError('');
    const result = await authenticatedFetchWithRetry<{ athletes: TeamHealthAthlete[]; team_gender?: string }>(
      getApiUrl('/api/team-health/'),
      { method: 'GET' },
      'fetching team health',
    );
    if (result.success && result.data) {
      setAthletes(result.data.athletes);
      if (result.data.team_gender === 'male' || result.data.team_gender === 'female') {
        setTeamGender(result.data.team_gender);
      } else {
        setTeamGender('mixed');
      }
    } else {
      setError(result.error || 'Failed to load team health data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTeamHealth(); }, [fetchTeamHealth]);

  // Sort helper
  const getSortValue = (a: TeamHealthAthlete, key: SortKey): number | string => {
    switch (key) {
      case 'name': return a.name.toLowerCase();
      case 'soreness': return a.latest_checkin?.soreness ?? -1;
      case 'rpe': return a.latest_checkin?.rpe ?? -1;
      case 'active_injury_count': return a.active_injury_count;
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  // Filter + sort
  const displayed = useMemo(() => {
    let list = [...athletes];

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a => a.name.toLowerCase().includes(q));
    }

    // Injury filter
    if (filterInjured === 'injured') {
      list = list.filter(a => a.active_injury_count > 0);
    } else if (filterInjured === 'healthy') {
      list = list.filter(a => a.active_injury_count === 0);
    }

    // Sort
    list.sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [athletes, search, filterInjured, sortKey, sortDir]);

  const toggleExpand = (id: number) => {
    setExpandedId(prev => {
      if (prev === id) {
        setSelectedRegion(null);
        return null;
      }
      setSelectedRegion(null);
      return id;
    });
  };

  const selectedAthlete = expandedId ? displayed.find(a => a.id === expandedId) ?? null : null;

  return (
    <>
      <Header userInfo={userInfo} isDark={isDark} />
      <DashboardLayout>
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold cgm-text-primary">Team Health & Injury Log</h1>
          <p className="text-sm cgm-text-secondary mt-1">
            Monitor athlete wellness and manage injury records
          </p>
        </div>

        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 cgm-text-secondary" />
            <input
              type="text"
              placeholder="Search athletes..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--card-border)] cgm-bg-secondary cgm-text-primary rounded-none"
            />
          </div>
          <select
            value={filterInjured}
            onChange={e => setFilterInjured(e.target.value as typeof filterInjured)}
            className="px-3 py-2 text-sm border border-[var(--card-border)] cgm-bg-secondary cgm-text-primary rounded-none"
          >
            <option value="all">All Athletes</option>
            <option value="injured">With Active Injuries</option>
            <option value="healthy">No Active Injuries</option>
          </select>
          <span className="text-xs cgm-text-secondary ml-auto">
            {displayed.length} athlete{displayed.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Error State */}
        {error && (
          <div className="flex items-center gap-2 p-3 mb-4 border border-red-500/30 bg-red-500/10 text-red-400 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12 cgm-text-secondary">Loading team health data...</div>
        )}

        {/* Empty State */}
        {!loading && !error && displayed.length === 0 && (
          <div className="text-center py-12 cgm-text-secondary">
            {athletes.length === 0
              ? 'No athletes on this team yet.'
              : 'No athletes match the current filters.'}
          </div>
        )}

        {/* Table */}
        {!loading && displayed.length > 0 && (
          <div className="flex flex-col md:flex-row gap-4">
            {/* Body Map Panel — desktop: left side sticky, mobile: above table */}
            {selectedAthlete && (
              <div className="w-full md:w-[280px] md:flex-shrink-0 transition-all duration-300">
                <div className="md:sticky md:top-[76px]">
                  <BodyMapPanel
                    athlete={selectedAthlete}
                    selectedRegion={selectedRegion}
                    onRegionClick={setSelectedRegion}
                    teamGender={teamGender}
                  />
                </div>
              </div>
            )}

            {/* Table Area */}
            <div className="flex-1 min-w-0">
              <div className="border border-[var(--card-border)] cgm-bg-primary overflow-hidden">
                {/* Header Row (desktop only — mobile uses inline labels) */}
                <div className="hidden md:grid grid-cols-[2fr_80px_80px_120px_1fr_80px] gap-2 px-4 py-2 text-[11px] uppercase tracking-wider cgm-text-secondary border-b border-[var(--card-border)] select-none">
                  <button onClick={() => handleSort('name')} className="text-left hover:cgm-text-primary transition-colors">
                    Athlete{sortIndicator('name')}
                  </button>
                  <button onClick={() => handleSort('soreness')} className="text-left hover:cgm-text-primary transition-colors">
                    Soreness{sortIndicator('soreness')}
                  </button>
                  <button onClick={() => handleSort('rpe')} className="text-left hover:cgm-text-primary transition-colors">
                    RPE{sortIndicator('rpe')}
                  </button>
                  <span>Pain Flags</span>
                  <button onClick={() => handleSort('active_injury_count')} className="text-left hover:cgm-text-primary transition-colors">
                    Injuries{sortIndicator('active_injury_count')}
                  </button>
                  <span></span>
                </div>

                {/* Athlete Rows */}
                {displayed.map(athlete => (
                  <React.Fragment key={athlete.id}>
                    <AthleteHealthRow
                      athlete={athlete}
                      isExpanded={expandedId === athlete.id}
                      onToggle={() => toggleExpand(athlete.id)}
                    />
                    {expandedId === athlete.id && (
                      <AthleteHealthExpanded
                        athlete={athlete}
                        onInjuryChange={fetchTeamHealth}
                        selectedRegion={selectedRegion}
                        onRegionSelect={setSelectedRegion}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}
      </DashboardLayout>
    </>
  );
};

export default TeamHealthDashboard;
